/**
 * Virtual airline mark: bundled tail-fin packs, or a PNG / SVG the operator uploads.
 *
 * A pack ref (`brand:<id>`) and a small data URL ride the career snapshot.
 * A larger file stays in the career save on this desk. The snapshot keeps the
 * name and a flag so other desks can say the mark was not copied.
 */

import { AIRLINE_LOGO_PACKS, type AirlineLogoPackAsset } from "./airlineLogoPacks.generated";
import type { CareerState } from "./types";

/** Data-URL length that still fits the existing snapshot without crowding it. */
export const AIRLINE_LOGO_SYNC_MAX = 96_000;

/** Same ceiling as the career logo field. Larger files are refused. */
export const AIRLINE_LOGO_LOCAL_MAX = 1_500_000;

export const AIRLINE_LOGO_KEPT_LOCAL_NOTE =
  "This logo stays on this computer. It is too large to sync, so the phone, the tablet, and other desks show the name only.";

const PNG_MAGIC = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

export function airlineLogoPacks(): readonly AirlineLogoPackAsset[] {
  return AIRLINE_LOGO_PACKS;
}

export function airlinePackById(id: string): AirlineLogoPackAsset | null {
  const key = id.trim().toLowerCase();
  return AIRLINE_LOGO_PACKS.find((pack) => pack.id === key) ?? null;
}

export function brandLogoRef(id: string): string {
  const pack = airlinePackById(id);
  return pack ? `brand:${pack.id}` : "";
}

/** Safe image URL for a bundled pack. Null when the id is not one of the packs. */
export function packLogoDataUrl(ref: string, onDark = false): string | null {
  const id = ref.trim().toLowerCase().replace(/^brand:/, "");
  const pack = airlinePackById(id);
  if (!pack) return null;
  return onDark ? pack.markReversedSvgUrl : pack.markSvgUrl;
}

export type AirlinePdfImage = { width: number; height: number; flate: Uint8Array };

function decodeB64(value: string): Uint8Array | null {
  const clean = value.replace(/\s+/g, "");
  if (!clean || clean.length % 4 === 1) return null;
  try {
    if (typeof Buffer !== "undefined") return new Uint8Array(Buffer.from(clean, "base64"));
    const bin = atob(clean);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
    return out;
  } catch {
    return null;
  }
}

/** White-paper plate for a pack mark. Uploaded plates use `pdfImageFromPlate`. */
export function packPdfImage(ref: string): AirlinePdfImage | null {
  const id = ref.trim().toLowerCase().replace(/^brand:/, "");
  const pack = airlinePackById(id);
  if (!pack?.pdf?.flate) return null;
  const flate = decodeB64(pack.pdf.flate);
  if (!flate?.length) return null;
  return { width: pack.pdf.width, height: pack.pdf.height, flate };
}

/** `128x128:<base64 flate RGB>` stored beside an upload so the quote PDF can draw it. */
export function pdfImageFromPlate(plate: unknown): AirlinePdfImage | null {
  if (typeof plate !== "string") return null;
  const match = /^(\d{1,4})x(\d{1,4}):([a-z0-9+/=]+)$/i.exec(plate.trim());
  if (!match) return null;
  const width = Number(match[1]);
  const height = Number(match[2]);
  if (!width || !height || width > 512 || height > 512) return null;
  const flate = decodeB64(match[3] ?? "");
  if (!flate?.length || flate.length > 80_000) return null;
  return { width, height, flate };
}

export function sanitizeAirlineLogoPlate(raw: unknown): string {
  const image = pdfImageFromPlate(raw);
  if (!image || typeof raw !== "string") return "";
  return raw.trim();
}

function bytesToB64(bytes: Uint8Array): string {
  if (typeof Buffer !== "undefined") return Buffer.from(bytes).toString("base64");
  let bin = "";
  for (let i = 0; i < bytes.length; i += 1) bin += String.fromCharCode(bytes[i] ?? 0);
  return btoa(bin);
}

/** RGBA from a canvas, composited on white, zlib-wrapped for a PDF image. */
export async function flatePlateFromRgba(
  width: number,
  height: number,
  rgba: Uint8ClampedArray | Uint8Array,
): Promise<string | null> {
  if (width < 1 || height < 1 || width > 512 || height > 512) return null;
  if (rgba.length < width * height * 4) return null;
  const rgb = new Uint8Array(width * height * 3);
  for (let p = 0, o = 0; p < width * height; p += 1, o += 4) {
    const alpha = (rgba[o + 3] ?? 255) / 255;
    rgb[p * 3] = Math.round((rgba[o] ?? 0) * alpha + 255 * (1 - alpha));
    rgb[p * 3 + 1] = Math.round((rgba[o + 1] ?? 0) * alpha + 255 * (1 - alpha));
    rgb[p * 3 + 2] = Math.round((rgba[o + 2] ?? 0) * alpha + 255 * (1 - alpha));
  }
  const zlibbed = await deflateZlib(rgb);
  if (!zlibbed) return null;
  const plate = `${width}x${height}:${bytesToB64(zlibbed)}`;
  return sanitizeAirlineLogoPlate(plate) ? plate : null;
}

async function deflateZlib(rgb: Uint8Array): Promise<Uint8Array | null> {
  const Stream = (globalThis as { CompressionStream?: typeof CompressionStream }).CompressionStream;
  if (typeof Stream !== "function") return null;
  try {
    const stream = new Stream("deflate");
    const writer = stream.writable.getWriter();
    const owned = new ArrayBuffer(rgb.byteLength);
    new Uint8Array(owned).set(rgb);
    await writer.write(new Uint8Array(owned));
    await writer.close();
    const blob = await new Response(stream.readable).arrayBuffer();
    return new Uint8Array(blob);
  } catch {
    return null;
  }
}

function looksLikePng(bytes: Uint8Array): boolean {
  if (bytes.length < 8) return false;
  return PNG_MAGIC.every((b, i) => bytes[i] === b);
}

function svgText(bytes: Uint8Array): string | null {
  let text = "";
  try {
    text = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return null;
  }
  const trimmed = text.trim();
  if (!/^<svg[\s>]/i.test(trimmed) && !/^<\?xml[^>]*>\s*<svg[\s>]/i.test(trimmed)) return null;
  return trimmed;
}

/** Reject script, remote loads, and active SVG. A plain drawing is fine. */
export function svgLogoIsSafe(svg: string): boolean {
  if (svg.length > AIRLINE_LOGO_LOCAL_MAX) return false;
  if (/<script|<foreignObject|<iframe|<object|<embed|<image|<use|<style|javascript:|on[a-z]+\s*=/i.test(svg)) {
    return false;
  }
  if (/\b(?:href|xlink:href)\s*=\s*["']?\s*(?:https?:|\/\/|data:)/i.test(svg)) return false;
  return true;
}

export type AirlineLogoUpload =
  | { ok: true; stored: string; keptLocal: boolean; note: string }
  | { ok: false; error: string };

function dataUrlFor(mime: "image/png" | "image/svg+xml", bytes: Uint8Array): string {
  return `data:${mime};base64,${bytesToB64(bytes)}`;
}

/**
 * PNG or SVG only. A short data URL syncs with the career. A longer one is
 * marked local. Anything past the career ceiling is refused.
 */
export function classifyAirlineLogoUpload(input: {
  bytes: Uint8Array;
  mime?: string;
  name?: string;
}): AirlineLogoUpload {
  const bytes = input.bytes;
  if (!bytes?.length) return { ok: false, error: "Choose a PNG or SVG logo." };
  if (bytes.length > AIRLINE_LOGO_LOCAL_MAX) {
    return { ok: false, error: "That logo is too large. Use a PNG or SVG under 1 MB." };
  }
  const hint = `${input.mime ?? ""} ${input.name ?? ""}`.toLowerCase();
  const png = looksLikePng(bytes) || /\.png\b|image\/png/.test(hint);
  const svgRaw = svgText(bytes);
  if (png && looksLikePng(bytes)) {
    const stored = dataUrlFor("image/png", bytes);
    if (stored.length > AIRLINE_LOGO_LOCAL_MAX) {
      return { ok: false, error: "That logo is too large. Use a PNG or SVG under 1 MB." };
    }
    const keptLocal = stored.length > AIRLINE_LOGO_SYNC_MAX;
    return {
      ok: true,
      stored,
      keptLocal,
      note: keptLocal ? AIRLINE_LOGO_KEPT_LOCAL_NOTE : "",
    };
  }
  if (svgRaw) {
    if (!svgLogoIsSafe(svgRaw)) {
      return { ok: false, error: "That SVG is not a plain logo. Use a drawing without scripts or remote images." };
    }
    const stored = dataUrlFor("image/svg+xml", new TextEncoder().encode(svgRaw));
    if (stored.length > AIRLINE_LOGO_LOCAL_MAX) {
      return { ok: false, error: "That logo is too large. Use a PNG or SVG under 1 MB." };
    }
    const keptLocal = stored.length > AIRLINE_LOGO_SYNC_MAX;
    return {
      ok: true,
      stored,
      keptLocal,
      note: keptLocal ? AIRLINE_LOGO_KEPT_LOCAL_NOTE : "",
    };
  }
  return { ok: false, error: "Use a PNG or SVG file." };
}

export function careerBrandingForSync<T extends Pick<CareerState, "airlineLogo" | "airlineLogoKeptLocal" | "airlineLogoPlate">>(
  career: T,
): T {
  const logo = career.airlineLogo ?? "";
  const oversized = logo.startsWith("data:") && logo.length > AIRLINE_LOGO_SYNC_MAX;
  if (oversized || logo === "asset:local") {
    return { ...career, airlineLogo: "", airlineLogoKeptLocal: true, airlineLogoPlate: career.airlineLogoPlate ?? "" };
  }
  if (logo.startsWith("brand:") || logo.startsWith("data:image/")) {
    return { ...career, airlineLogoKeptLocal: false };
  }
  return career;
}

/**
 * A pull must not drop a logo that never left this desk.
 * A logo that arrived in the snapshot replaces it.
 */
export function mergeLocalAirlineLogo(local: CareerState, incoming: CareerState): CareerState {
  const nextLogo = incoming.airlineLogo ?? "";
  if (nextLogo.startsWith("brand:") || nextLogo.startsWith("data:image/")) {
    return { ...incoming, airlineLogo: nextLogo, airlineLogoKeptLocal: false };
  }
  const localLogo = local.airlineLogo ?? "";
  const localFile =
    localLogo.startsWith("data:image/") &&
    (local.airlineLogoKeptLocal || localLogo.length > AIRLINE_LOGO_SYNC_MAX);
  if (localFile) {
    return {
      ...incoming,
      airlineLogo: localLogo,
      airlineLogoKeptLocal: true,
      airlineLogoPlate: incoming.airlineLogoPlate || local.airlineLogoPlate || "",
    };
  }
  return incoming;
}

/** Logo ref safe to put on a snapshot, the phone, and the tablet. */
export function syncableAirlineLogo(career: Pick<CareerState, "airlineLogo" | "airlineLogoKeptLocal"> | null | undefined): string {
  if (!career || career.airlineLogoKeptLocal) return "";
  const logo = career.airlineLogo ?? "";
  if (!logo || logo === "asset:local") return "";
  if (logo.startsWith("data:") && logo.length > AIRLINE_LOGO_SYNC_MAX) return "";
  if (logo.startsWith("brand:") || logo.startsWith("data:image/")) return logo;
  return "";
}

export function escapeBrandText(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Letterhead for an HTML print page. Empty when there is nothing to draw. */
export function brandLetterheadHtml(name: string, logoSrc: string | null | undefined): string {
  const label = name.replace(/\s+/g, " ").trim();
  const src = logoSrc?.trim() ?? "";
  if (!label && !src) return "";
  const img = src.startsWith("data:image/")
    ? `<img class="brand-logo" src="${escapeBrandText(src)}" alt="" />`
    : "";
  const title = label ? `<p class="brand-name">${escapeBrandText(label)}</p>` : "";
  return `<div class="brand-lockup">${img}${title}</div>`;
}

export function quoteLogoImage(input: { logoRef?: string | null; logoPlate?: string | null }): AirlinePdfImage | null {
  const fromPack = input.logoRef ? packPdfImage(input.logoRef) : null;
  if (fromPack) return fromPack;
  return pdfImageFromPlate(input.logoPlate);
}
